/**
 * Mindfulness Sprint Tool
 *
 * 2-10 minute micro-practices with guided prompts.
 */

import { StepFlow, CountdownTimer, BreathingAnimation, formatTime, saveToolEntry, initButtonGroup } from './tools-common';

var GUIDED_PROMPTS = {
    breath: [
        { at: 1.0, text: 'Settle in. Close your eyes if comfortable.' },
        { at: 0.9, text: 'Bring your attention to where you feel your breath most clearly.' },
        { at: 0.7, text: 'Follow each inhale and exhale. No need to change anything.' },
        { at: 0.5, text: 'When your mind wanders, gently bring it back. This is the practice.' },
        { at: 0.3, text: 'Notice the pause between breaths. Rest in that stillness.' },
        { at: 0.1, text: 'Begin to widen your awareness. You are here.' },
    ],
    body: [
        { at: 1.0, text: 'Close your eyes. Take one deep breath.' },
        { at: 0.85, text: 'Bring attention to the top of your head. Notice any sensations.' },
        { at: 0.7, text: 'Slowly move awareness down: forehead, jaw, neck, shoulders.' },
        { at: 0.5, text: 'Notice your chest, belly, arms, hands. No need to change anything.' },
        { at: 0.3, text: 'Continue down through your legs to your feet.' },
        { at: 0.1, text: 'Take a moment to feel your whole body as one. You are grounded.' },
    ],
    sounds: [
        { at: 1.0, text: 'Close your eyes. Let sounds come to you.' },
        { at: 0.8, text: 'Notice the closest sound. Then the furthest.' },
        { at: 0.6, text: 'Let sounds arise and pass without labeling them.' },
        { at: 0.4, text: 'Notice the silence between sounds.' },
        { at: 0.2, text: 'Sounds anchor you to the present moment.' },
        { at: 0.05, text: 'Gently return your awareness. The practice is complete.' },
    ],
    'urge-surfing': [
        { at: 1.0, text: 'Notice the urge or craving you feel right now.' },
        { at: 0.85, text: 'Where do you feel it in your body? Get curious.' },
        { at: 0.65, text: 'Imagine the urge as a wave. It rises, peaks, and falls.' },
        { at: 0.45, text: 'You don\'t have to act on it. Just observe.' },
        { at: 0.25, text: 'The wave is already receding. Stay with it.' },
        { at: 0.05, text: 'You surfed it. The urge passes on its own.' },
    ],
};

export function initMindfulnessSprint() {
    var root = document.getElementById('mindfulness-sprint');
    if (!root) return;

    var flow = new StepFlow(root);
    var timer = null;
    var breathing = null;
    var selectedDuration = 300;
    var selectedStyle = 'breath';
    var preAnxiety = 3;  // 1-5 scale (Figma)
    var postAnxiety = 3;

    initButtonGroup(root, '.tool-duration-btn', function (btn) {
        selectedDuration = parseInt(btn.dataset.duration, 10);
    });

    initButtonGroup(root, '.tool-style-btn', function (btn) {
        selectedStyle = btn.dataset.style;
    });

    initButtonGroup(root, '.tool-anxiety-btn', function (btn) {
        preAnxiety = parseInt(btn.dataset.value, 10);
    });

    initButtonGroup(root, '.tool-post-anxiety-btn', function (btn) {
        postAnxiety = parseInt(btn.dataset.value, 10);
    });

    function getPrompt(remaining, total) {
        var prompts = GUIDED_PROMPTS[selectedStyle] || GUIDED_PROMPTS.breath;
        var ratio = remaining / total;
        var current = prompts[0];
        for (var i = 0; i < prompts.length; i++) {
            if (ratio <= prompts[i].at) current = prompts[i];
        }
        return current;
    }

    // Start
    root.querySelector('.tool-start-btn').addEventListener('click', function () {
        var activePre = root.querySelector('.tool-anxiety-btn.active');
        if (activePre) preAnxiety = parseInt(activePre.dataset.value, 10);
        flow.show('session');

        var phaseText = document.getElementById('mindfulness-phase-text');
        var display = document.getElementById('mindfulness-timer-display');
        var stressPill = document.getElementById('mindfulness-stress-pill');

        display.textContent = formatTime(selectedDuration);
        if (stressPill) {
            var pct = Math.round((preAnxiety / 5) * 100);
            stressPill.textContent = 'Stress ' + pct + '%';
        }

        timer = new CountdownTimer(selectedDuration, function (remaining, total) {
            display.textContent = formatTime(remaining);
            var p = getPrompt(remaining, total);
            if (phaseText) phaseText.textContent = p.text;
        }, function () {
            endSession();
        });
        timer.start();

        // Breathing animation only for breath and urge-surfing styles
        if (selectedStyle === 'breath' || selectedStyle === 'urge-surfing') {
            breathing = new BreathingAnimation(
                document.getElementById('mindfulness-breathing-circle'),
                document.getElementById('mindfulness-breath-instruction'),
                document.getElementById('mindfulness-breath-counter')
            );
            breathing.start();
            document.getElementById('mindfulness-breathing-circle').style.display = '';
        } else {
            document.getElementById('mindfulness-breathing-circle').style.display = 'none';
        }
    });

    function endSession() {
        if (timer) timer.stop();
        if (breathing) breathing.stop();
        flow.show('post');
    }

    // Stop early
    root.querySelectorAll('.tool-stop-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
            endSession();
        });
    });

    // Complete from post screen
    var completeBtn = document.getElementById('mindfulness-complete-btn');
    if (completeBtn) {
        completeBtn.addEventListener('click', function () {
            var activePost = root.querySelector('.tool-post-anxiety-btn.active');
            if (activePost) postAnxiety = parseInt(activePost.dataset.value, 10);

            var summaryEl = document.getElementById('mindfulness-summary');
            if (summaryEl) {
                summaryEl.innerHTML = '';
                var styleLabel = summaryEl.appendChild(document.createElement('div'));
                styleLabel.className = 'tool-app__summary-item';
                styleLabel.innerHTML = '<span class="tool-app__summary-label">Style</span><span class="tool-app__summary-value">' + (selectedStyle === 'urge-surfing' ? 'urge surfing' : selectedStyle) + '</span>';
                var durationLabel = summaryEl.appendChild(document.createElement('div'));
                durationLabel.className = 'tool-app__summary-item';
                durationLabel.innerHTML = '<span class="tool-app__summary-label">Duration</span><span class="tool-app__summary-value">' + formatTime(selectedDuration) + '</span>';
                var anxietyLabel = summaryEl.appendChild(document.createElement('div'));
                anxietyLabel.className = 'tool-app__summary-item';
                anxietyLabel.innerHTML = '<span class="tool-app__summary-label">Anxiety</span><span class="tool-app__summary-value">' + preAnxiety + ' → ' + postAnxiety + '</span>';
            }

            saveToolEntry({
                tool_slug: 'mindfulness-sprint',
                entry_type: 'session',
                anxiety_before: preAnxiety,
                anxiety_after: postAnxiety,
                data: {
                    style: selectedStyle,
                    duration: selectedDuration,
                },
            });

            flow.show('complete');
        });
    }

    // Restart
    root.querySelectorAll('.tool-restart-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
            flow.reset();
        });
    });
}
